import fetchAllBrands from '@dehaat/kisan-app-bl/api/fetchAllBrands';
import fetchAllProductCrops from '@dehaat/kisan-app-bl/api/fetchAllProductCrops';
import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import Categories from '@dehaat/kisan-app-bl/models/CategoriesV2';
import { Brand } from '@dehaat/kisan-app-bl/models/Product';
import { Crop } from '@dehaat/kisan-app-bl/models/Product';
import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useEffect, useMemo, useState } from 'react';
import AppBackButton from '@/components/AppBackButton';
import MyCart from '@/components/cart/MyCart';
import CheckoutPopup from '@/components/CheckoutPopup';
import MagnifyingGlass from '@/components/icons/MagnifyingGlass';
import LazyImage from '@/components/LazyImage';
import CurrentLocation from '@/components/navigation/CurrentLocation';
import Profile from '@/components/navigation/Profile';
import ProductFilter from '@/components/products/ProductFilter';
import ProductResults from '@/components/products/ProductResults';
import { COOKIE_MAX_AGE_1YEAR, CURRENT_VENDOR_KEY, DC_ID, IS_PREBOOK_FLOW, LAT_LNG_COOKIE_NAME, NAVIGATION_FROM_APP, NAVIGATION_FROM_APP_CODE, PRODUCTS_QUERY_OPEN, REDIRECT_URI_KEY, VENDOR_ID_COOKIE_NAME } from '@/constants/common';
import { FilterTypes } from '@/constants/filterTypes';
import { CartContext } from '@/context/CartProvider';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import useIsMobile from '@/hooks/useIsMobile';
import useSearchProducts, { createSearchProductQuery } from '@/hooks/useSearchProducts';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import scrollStyles from '@/styles/HideScrollbar.module.css';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import { ProductListTypeEnum } from '@/utils/analytics/types';
import fetchAllCategoriesV2 from '@/utils/api/fetchallCategories';
import fetchAllProducts from '@/utils/api/fetchAllProducts';
import fetchBrandBySlug from '@/utils/api/fetchBrandBySlug';
import getProductDetailsFromSlug, { ProductSlugDetails } from '@/utils/api/getProductDetailsFromSlug';
import submitCampaignDetails from '@/utils/api/submitCampaignDetails';
import { isCartEmpty } from '@/utils/cart';
import { getCookieValue, getLocalStorageKey, isDigiAcreUser, isFromVrApp, resolveAsString } from '@/utils/helper';
import getCategoryFilterQuery from '@/utils/helpers/getCategoryFilterQuery';
import parseQuery from '@/utils/helpers/parseQuery';
interface Props {
  brandInfo?: Brand;
  categories: Categories[];
  brands: Brand[];
  crops: Crop[];
}

// "CALORGREEN-PVT-LTD-2280" -> "CALORGREEN PVT LTD"
const getBrandNameFromSlug = (slug?: string | string[]) => resolveAsString(slug).replace(/-\d+$/, '').replace(/-+/g, ' ').trim();
const BrandSpecificProductListing = ({
  brandInfo,
  categories
}: Props) => {
  const {
    t
  } = useTranslation('products');
  const [brandId, setBrandId] = useState(brandInfo?.id);
  const [crops, setCrops] = useState<Crop[]>([]);
  const {
    asPath,
    query,
    replace,
    locale
  } = useRouter();
  const {
    brandSlug,
    c
  } = query;
  const brandSlugEnc = encodeURIComponent(resolveAsString(brandSlug));
  const {
    cartItems,
    isPrebookFlow
  } = useContext(CartContext);
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(c ? Number(c) : categories.length > 0 ? categories[0].id : undefined);
  const [count, setTotalCount] = useState<number>(0);
  const vendorId = getCookieValue(VENDOR_ID_COOKIE_NAME) as string;
  const [prebookCategories, setPrebookCategories] = useState<Categories[]>();
  const sidebarCategories = isPrebookFlow && prebookCategories ? prebookCategories : categories;
  const isDAUser = isDigiAcreUser();
  const isVR = isFromVrApp(getCookieValue(NAVIGATION_FROM_APP_CODE));
  const fromApp = useMemo(() => Boolean(getCookieValue(NAVIGATION_FROM_APP)) || Boolean(getCookieValue(NAVIGATION_FROM_APP_CODE)) || isDAUser, [isDAUser]);
  const {
    isEnabled
  } = useFeatureFlags();
  const isNewSearch = isVR || isEnabled('enable_new_search', true);
  const {
    isMobile,
    isWindowLoading
  } = useIsMobile();
  const {
    searchedProducts,
    searchingProducts,
    totalCount,
    infiniteScrollRef,
    appliedBrandIds,
    appliedCategoryIds,
    appliedCropIds
  } = useSearchProducts(true, 10, undefined, undefined, brandId);
  const {
    trackCustomEvent
  } = useTriggerEvents();
  useEffect(() => {
    if (selectedCategory) {
      const queryString = asPath.split('?')[1] || '';
      const updatedQuery = getCategoryFilterQuery(queryString, selectedCategory.toString());
      replace({
        pathname: `/products/brands/${brandSlugEnc}`,
        query: updatedQuery
      }, undefined, {
        shallow: true
      });
    }
  }, [selectedCategory]);

  // The prebook flow lives in localStorage; getServerSideProps only sees it
  // while `prebook=true` is in the URL, and the category effect above drops
  // that param. Fetch the prebook sidebar here so it survives in-app
  // navigation, reloads and page-data refreshes.
  useEffect(() => {
    if (!isPrebookFlow || query.prebook === 'true') return;
    let ignore = false;
    fetchAllCategoriesV2({
      brand_ids: resolveAsString(brandSlug),
      lang: locale,
      vendor_id: vendorId && vendorId !== 'null' ? vendorId : undefined,
      isprebook: true
    }).then(response => {
      if (!ignore && response) setPrebookCategories(response);
    });
    return () => {
      ignore = true;
    };
  }, [isPrebookFlow, query.prebook, brandSlug, locale, vendorId]);
  useEffect(() => {
    if (!selectedCategory && sidebarCategories.length > 0) {
      setSelectedCategory(sidebarCategories[0].id);
    }
  }, [sidebarCategories]);
  useEffect(() => {
    if (!brandId) return;
    const fetchBrandcount = async () => {
      const isprebook = !!getLocalStorageKey(IS_PREBOOK_FLOW);
      try {
        const params = {
          vendor_id: vendorId || '',
          page_size: 1,
          brand_id: brandId,
          category_id: '',
          lang: locale ? locale : 'en',
          isprebook,
          iscount: true,
          crop_id: '',
          collection_id: ''
        };
        const queryString = createSearchProductQuery(params);
        const response = await fetchAllProducts({
          query: queryString
        });
        setTotalCount(response.count || 0);
      } catch (error) {
        console.error('Error fetching category count:', error);
        setTotalCount(0);
      }
    };
    fetchBrandcount();
  }, [brandId]);
  useEffect(() => {
    const getProductId = async (slug: string) => {
      const productDetails: ProductSlugDetails[] = await getProductDetailsFromSlug(slug, 'BRAND');
      return productDetails[0]?.entity_id;
    };
    if (!brandId && brandSlugEnc) {
      getProductId(brandSlugEnc).then(id => {
        setBrandId(id?.toString());
      });
    }
  }, [brandId, brandSlugEnc]);
  const handleCategoryClick = (id: number) => {
    setSelectedCategory(id);
    window.scrollTo(0, 0);
  };
  useEffect(() => {
    const {
      requester_id,
      farmer_ph_no
    } = parseQuery(query, ['requester_id', 'farmer_ph_no']);
    if (requester_id && farmer_ph_no) submitCampaignDetails({
      requester_id,
      farmer_ph_no
    });
  }, [query]);
  useEffect(() => {
    if (brandInfo?.id && brandInfo?.name) {
      const eventNames = {
        brandId: brandInfo.id,
        brandName: brandInfo.name
      };
      trackCustomEvent(CustomEventTypes.USER_LANDED_ON_SHOP_BY_BRAND, JSON.stringify(eventNames));
    }
  }, [brandInfo]);
  useEffect(() => {
    const getCrops = async () => {
      const latLong = getCookieValue(LAT_LNG_COOKIE_NAME);
      const cropsData = await fetchAllProductCrops(locale, latLong, vendorId);
      setCrops(cropsData);
    };
    getCrops();
  }, [locale, vendorId]);
  useEffect(() => {
    const vendorData = localStorage.getItem(CURRENT_VENDOR_KEY);
    const dcId = vendorData ? JSON.parse(vendorData)?.entity_id : undefined;
    if (dcId) {
      document.cookie = `${DC_ID}=${dcId}; max-age=${COOKIE_MAX_AGE_1YEAR}; path=/`;
    }
  }, []);
  const brandName = brandInfo?.name || getBrandNameFromSlug(brandSlug);
  return <main className="relative bg-neutral-10 h-screen" data-sentry-component="BrandSpecificProductListing" data-sentry-source-file="[brandSlug].tsx">
      <div className="top-0 left-0 z-50 fixed flex justify-between items-center bg-white shadow-[0_6px_20px_rgba(0,0,0,0.05)] px-4 py-4 w-full">
        <div className="flex items-center">
          <AppBackButton data-sentry-element="AppBackButton" data-sentry-source-file="[brandSlug].tsx" />
          <CurrentLocation showAddressDetails={false} fromScreen={asPath} data-sentry-element="CurrentLocation" data-sentry-source-file="[brandSlug].tsx" />

          <div className="relative ml-4 w-8 h-8">
            <LazyImage alt={brandName} fallBackIcon={<div className="flex justify-center items-center bg-neutral-300 rounded-full w-8 h-8 overflow-hidden">
                  <span className="text-xl">
                    {brandName.charAt(0).toUpperCase()}
                  </span>
                </div>} imageUrl={brandInfo?.imageUrl} className="rounded-full w-8 h-8 object-cover" data-sentry-element="LazyImage" data-sentry-source-file="[brandSlug].tsx" />
          </div>
          <div className="ml-2">
            <h1 className="font-nato-semibold text-sm capitalize">
              {brandName}
            </h1>
          </div>
        </div>

        <div className="flex gap-4">
          {!isWindowLoading && !isMobile && <MyCart />}
          <Link href={isNewSearch ? `/s?` : `/?so=${PRODUCTS_QUERY_OPEN}&${REDIRECT_URI_KEY}=${asPath}`} aria-label={t('search_products')} data-sentry-element="Link" data-sentry-source-file="[brandSlug].tsx">
            <div className="p-2 border border-neutral-20 rounded-xl">
              <MagnifyingGlass className="fill-transparent stroke-neutral-80 w-6 h-6" role="presentation" aria-hidden="true" data-sentry-element="MagnifyingGlass" data-sentry-source-file="[brandSlug].tsx" />
            </div>
          </Link>
          {!fromApp && <Profile />}
        </div>
      </div>

      <section className={`h-screen w-full m-auto bg-white flex flex-col relative bg-gray-10 ${isMobile && !isCartEmpty(cartItems) ? 'pb-16' : ''} ${isMobile ? 'pt-18' : 'pt-[84px]'}`}>
        <ProductFilter appliedBrandIds={appliedBrandIds} appliedCategoryIds={appliedCategoryIds} appliedCropIds={appliedCropIds} crops={crops} pathname={`/products/brands/${brandSlugEnc}`} defaultHighlighted={FilterTypes.CROPS} productCount={count || 0} data-sentry-element="ProductFilter" data-sentry-source-file="[brandSlug].tsx" />
        <div className={`flex h-full w-full overflow-hidden relative`}>
          <div className={`max-w-[22%] overflow-scroll bg-white ${scrollStyles.scroll}`}>
            {sidebarCategories?.map(({
            id,
            name,
            image_url,
            app_display_name
          }) => <div onClick={() => handleCategoryClick(id)} key={id} className={`p-4 flex flex-col justify-center items-center relative ${id === selectedCategory ? 'bg-primary-10' : ''}`}>
                  {id === selectedCategory && <div className="left-0 absolute bg-primary-100 rounded-lg w-1 h-10" />}
                  <div className="relative mb-2 w-12 h-12">
                    <LazyImage alt={name} fallBackIcon={<div className="flex justify-center items-center bg-neutral-300 rounded-full w-12 h-12 overflow-hidden">
                          <span className="text-4xl">{name[0]}</span>
                        </div>} imageUrl={image_url} className="rounded-full w-12 h-12 object-cover" />
                  </div>
                  <p className="text-neutral-60 text-xs text-center">
                    {app_display_name}
                  </p>
                </div>)}
          </div>
          <div className="flex-1 h-full overflow-scroll">
            <ProductResults products={searchedProducts} loading={searchingProducts} cartEmpty={true} nextLoaderRef={infiniteScrollRef} noResults={<span className="inline-block flex-1 mt-4 px-4 text-center">
                  {t('no_filtered_products')}
                </span>} listType={ProductListTypeEnum.PRODUCT} data-sentry-element="ProductResults" data-sentry-source-file="[brandSlug].tsx" />
          </div>
        </div>
      </section>
      <CheckoutPopup data-sentry-element="CheckoutPopup" data-sentry-source-file="[brandSlug].tsx" />
    </main>;
};
export const getServerSideProps: GetServerSideProps = async ({
  req: {
    cookies
  },
  locale,
  query
}) => {
  let categories: Categories[] = [];
  const latLong = cookies[LAT_LNG_COOKIE_NAME];
  const vendorId = cookies[VENDOR_ID_COOKIE_NAME] !== 'null' ? cookies[VENDOR_ID_COOKIE_NAME] : undefined;
  try {
    const [brandsRes, cropCategoriesRes] = await Promise.allSettled([fetchAllBrands(locale, latLong, vendorId), fetchAllCategoriesV2({
      brand_ids: resolveAsString(query.brandSlug),
      lang: locale,
      vendor_id: vendorId,
      // Same flag as /hyperlocal/v1/products, so prebook-only vendors get
      // their categories too
      ...(query.prebook === 'true' && {
        isprebook: true
      })
    })]);

    // Brands
    const brands = brandsRes.status === 'fulfilled' ? brandsRes.value : [];
    const brandSlug = resolveAsString(query.brandSlug);
    // Brands without regular products at the vendor (prebook brands) are not
    // in its brand list
    const brandInfo = brands.find(({
      slug
    }) => slug?.slug === brandSlug) || (brandSlug ? await fetchBrandBySlug(brandSlug, locale || DEFAULT_LANG).catch(() => undefined) : undefined);

    // Crop Categories
    const cropCategories = cropCategoriesRes.status === 'fulfilled' ? cropCategoriesRes.value : [];
    if (cropCategories) {
      categories = cropCategories;
    }
    if (brandInfo) {
      return {
        props: {
          categories,
          brandInfo,
          title: `${brandInfo.name} Products`
        }
      };
    }
    return {
      props: {
        categories,
        title: `${getBrandNameFromSlug(query.brandSlug)} Products`
      }
    };
  } catch {
    return {
      redirect: {
        destination: '/500',
        permanent: false
      }
    };
  }
};
export default BrandSpecificProductListing;